/**
 * SavePromptDialog - Dialog to save current transcript as a refinement prompt
 */

import { MAX_PROMPT_LENGTH, MIN_PROMPT_LENGTH } from '@refinevoice/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyleStore } from '../store/useStyleStore';
import './SavePromptDialog.css';

interface SavePromptDialogProps {
  promptText: string;
  onClose: () => void;
}

export function SavePromptDialog({ promptText, onClose }: SavePromptDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const { addCustomStyle } = useStyleStore();
  const { t } = useTranslation();

  const handleSave = () => {
    setError('');

    const result = addCustomStyle(name, promptText);

    if (!result.success) {
      if (result.errorKey) {
        const errorMessage = t(result.errorKey, result.errorParams || {});
        setError(typeof errorMessage === 'string' ? errorMessage : String(errorMessage));
      } else {
        setError(t('styleErrors.saveFailed'));
      }
      return;
    }

    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const promptLength = promptText.length;
  const isValidLength = promptLength >= MIN_PROMPT_LENGTH && promptLength <= MAX_PROMPT_LENGTH;

  return (
    <div
      className="dialog-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="dialog-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <h2 className="dialog-title">{t('savePromptDialog.title')}</h2>

        <div className="dialog-body">
          <div className="prompt-preview">
            <div className="preview-label">
              {t('savePromptDialog.previewLabel', 'Prompt Preview:')}
            </div>
            <div className="preview-text">{promptText}</div>
            <div className={`char-count ${!isValidLength ? 'invalid' : ''}`}>
              {promptLength} / {MAX_PROMPT_LENGTH} {t('savePromptDialog.characters', 'characters')}
              {promptLength < MIN_PROMPT_LENGTH && (
                <span className="char-warning">
                  {' '}
                  ({t('savePromptDialog.minimum', 'minimum')} {MIN_PROMPT_LENGTH})
                </span>
              )}
            </div>
          </div>

          {!isValidLength && (
            <div className="validation-error">
              {t('styleErrors.promptTooShort', { min: MIN_PROMPT_LENGTH, max: MAX_PROMPT_LENGTH })}
            </div>
          )}

          <div className="name-input-group">
            <label htmlFor="prompt-name" className="input-label">
              {t('savePromptDialog.nameLabel', 'Name this style:')}
            </label>
            <input
              id="prompt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('savePromptDialog.namePlaceholder', 'e.g., Academic Style')}
              className="name-input"
              maxLength={50}
            />
          </div>

          {error && <div className="save-error">{error}</div>}
        </div>

        <div className="dialog-actions">
          <button type="button" onClick={onClose} className="btn-cancel">
            {t('savePromptDialog.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-save"
            disabled={!name.trim() || !isValidLength}
          >
            {t('savePromptDialog.save')} ✓
          </button>
        </div>
      </div>
    </div>
  );
}
